"use client"

import { useState, useRef } from "react"
import SignInButton from "@/app/components/SignInButton"
import { postComment } from "@/app/actions/post-comment"
import { updateComment } from "@/app/actions/update-comment"
import { useRouter } from "next/navigation"

interface CommentsClientProps {
  type: "signin-link" | "comment-form" | "edit-button"
  storyId: string
  canComment: boolean
  isLoggedIn: boolean
  commentId?: string
}

export default function CommentsClient({ type, storyId, canComment, isLoggedIn, commentId }: CommentsClientProps) {
  const router = useRouter()
  const [commentText, setCommentText] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [isUpdating, setIsUpdating] = useState(false)
  const [successCommentId, setSuccessCommentId] = useState<string | null>(null)

  // Scroll position preservation
  const scrollPositionRef = useRef<number>(0)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!commentText.trim()) {
      return
    }

    setIsSubmitting(true)

    // Save current scroll position
    scrollPositionRef.current = window.scrollY

    try {
      const result = await postComment(storyId, commentText.trim())

      if (result.success) {
        setCommentText("")
        // Refresh the page to show new comment
        router.refresh()
      }
    } catch {
      // Error handling could be added here if needed
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditClick = (commentId: string) => {
    setEditingCommentId(commentId)

    // Get rawText from data-raw attribute
    const commentDiv = document.querySelector(`[data-comment-id="${commentId}"]`) as HTMLElement
    const rawText = commentDiv?.getAttribute("data-raw") || ""
    setEditText(rawText)
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setEditText("")
  }

  const handleUpdateSubmit = async (commentId: string) => {
    if (!editText || !editText.trim()) {
      return
    }

    setIsUpdating(true)

    // Save current scroll position
    scrollPositionRef.current = window.scrollY

    try {
      const result = await updateComment(storyId, commentId, editText.trim())

      if (result.success) {
        setEditingCommentId(null)
        setEditText("")
        setSuccessCommentId(commentId)

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessCommentId(null), 3000)

        // Refresh the page to show updated comment
        router.refresh()
      }
    } catch {
      // Error handling could be added here if needed
    } finally {
      setIsUpdating(false)
    }
  }

  if (type === "signin-link") {
    return (
      <SignInButton className="text-interest-blue hover:text-interest-dark-blue font-inherit cursor-pointer border-none bg-transparent p-0 underline">
        sign up
      </SignInButton>
    )
  }

  if (type === "comment-form") {
    return (
      <>
        {canComment ? (
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="mb-4">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Enter your comment here (Markdown supported)..."
                rows={4}
                className="focus:ring-interest-blue resize-vertical min-h-[100px] w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={isSubmitting || !commentText.trim()}
                className="bg-interest-blue hover:bg-interest-dark-blue rounded-md px-6 py-2 text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {isSubmitting ? "Posting..." : "Post"}
              </button>
            </div>
          </form>
        ) : (
          <div className="mb-8 rounded-md border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-600">
              {isLoggedIn ? (
                "You need comment permissions to post comments."
              ) : (
                <>
                  Please{" "}
                  <SignInButton className="text-interest-blue hover:text-interest-dark-blue font-inherit cursor-pointer border-none bg-transparent p-0 underline">
                    log in
                  </SignInButton>{" "}
                  to post comments.
                </>
              )}
            </p>
          </div>
        )}
      </>
    )
  }

  if (type === "edit-button" && commentId) {
    return (
      <>
        <button
          onClick={() => handleEditClick(commentId)}
          className="text-interest-blue hover:text-interest-dark-blue cursor-pointer border-none bg-transparent p-0 text-sm underline"
          disabled={!!editingCommentId || isUpdating}
        >
          edit
        </button>

        {editingCommentId === commentId && (
          <div className="mt-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className="focus:ring-interest-blue resize-vertical mb-2 min-h-[100px] w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2"
              disabled={isUpdating}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleUpdateSubmit(commentId)}
                disabled={isUpdating || !editText || !editText.trim()}
                className="bg-interest-blue hover:bg-interest-dark-blue rounded-md px-4 py-1 text-sm text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isUpdating}
                className="rounded-md border border-gray-300 px-4 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                Cancel
              </button>
            </div>

            {/* Success message for this specific comment */}
            {successCommentId === commentId && (
              <div className="mt-2 rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-700">
                Comment updated successfully!
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  return null
}
